import { AITrace } from "../models/aiTrace";
import {
  SiteDemand,
  SiteElectricalSystem,
  SiteOwnership,
} from "../models/site";
import axiosConfig from "./axiosConfig";

export interface SiteDemandView {
  id: string;
  name: string;
  nearbySiteIDs: string[];
  esys: {
    [key: string]: Pick<
      SiteElectricalSystem,
      "idx" | "demandIdx" | "importGridConnectionId"
    >;
  };
  demand: {
    [key: string]: Pick<SiteDemand, "buildingProfile" | "existingPvKwp">;
  };
  siteOwnerships: SiteOwnership[];
}
export interface HhDataSiteMapping {
  demandIdx: number;
  demandUuid: string;
  site: SiteDemandView;
}

export interface AutoModeProvenance {
  version: number;
  ranAt: string;
  ranBy: string;
  // `detection` is only present on curves produced by /process_auto. Curves produced
  // by /split_into_curves carry `source: "multi_curve_split"` (+ a `label`) instead,
  // and the FE renders a simpler badge title.
  detection?: {
    period: { value: string; confidence: string; reasons: string[] };
    unit: { value: string; confidence: string; reasons: string[] };
  };
  source?: string;
  label?: string;
  // Set when an AI-proposed fix produced this curve's config. Persisted on the autoMode block
  // (not just the temp attempt-history) so it survives commit and shows on the processed table.
  autoFixed?: boolean;
  autoFixedAt?: string;
  autoFixedBy?: string;
}

export interface HhDataItem {
  guid: string;
  siteMappings: HhDataSiteMapping[];
  fileName?: string;
  isScript?: boolean;
  uploadedBy?: string;
  processedTime?: number;
  processedBy?: string;
  // % of the normalised year derived directly from customer data (rest is filled).
  // Used by the FE for the fill-coverage column with green/yellow/red bands.
  customerDataFraction?: number;
  // Set when the curve was produced by Auto mode rather than operator-configured.
  autoMode?: AutoModeProvenance;
  // AI-assist state derived from the committed file's attempt_history.json (copied into
  // the permanent bucket on commit). Lets the processed-data row restore the
  // [Review file analysis] button + content for any operator, the same as before commit.
  autoAttemptCount?: number;
  autoFixAttempted?: boolean;
  autoFixed?: boolean;
  latestAnalysis?: {
    summary?: string;
    details?: string;
    proposed_config?: Record<string, unknown> | null;
    proposed_config_rationale?: string | null;
    trace?: AITrace | null;
  };
}

export interface HhDataResponse {
  hhData: HhDataItem[];
  availableSites: SiteDemandView[];
}

const getHhData = async (authToken: string): Promise<HhDataResponse> => {
  const { data } = await axiosConfig.get(`/hh_data`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
  return data;
};

export default getHhData;
